import { useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Stack,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useForm, Controller } from "react-hook-form";
import { api } from "@/helper/api";
import GenericButton from "@/components/forms/inputs/button/genericButton";
import { useErrorModal } from "@/components/forms/errorHandling/useErrorModal";
import styles from "@/components/todoTemplate/todoTemplateModal.module.css";
import { InputWithRef } from "@/components/forms/inputsWithRef/inputWithRef";
import { TextAreaWithRef } from "@/components/forms/inputsWithRef/textAreaWithRef";
import useFocusFirstInvalidField from "@/helper/customHooks/useFocusFirstInvalidField";

interface Props {
  open: boolean;
  onClose: () => void;
  mode: "create" | "edit";
  initialData?: any;
  onSuccess: () => void;
}

export default function TodoTemplateModal({
  open,
  onClose,
  mode,
  initialData,
  onSuccess,
}: Props) {
  const { t } = useTranslation();
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm<{
    name: string;
    title: string;
    description: string;
  }>({
    shouldFocusError: false,
    defaultValues: {
      name: "",
      title: "",
      description: "",
    },
    mode: "onChange",
  });

  const { registerValidationTarget, focusFirstInvalidField } =
    useFocusFirstInvalidField({
      fieldOrder: ["name", "title", "description"],
    });

  const { handleError, errorModal } = useErrorModal();

  useEffect(() => {
    if (open) {
      if (mode === "edit" && initialData) {
        const description = initialData.description;
        const normalizedDescription =
          typeof description === "string"
            ? description
            : description && typeof description === "object"
            ? JSON.stringify(description)
            : "";

        reset({
          name: initialData.name || "",
          title: initialData.title || "",
          description: normalizedDescription,
        });
      } else {
        reset({
          name: "",
          title: "",
          description: "",
        });
      }
    }
  }, [open, mode, initialData, reset]);

  const onSubmit = async (data: any) => {
    try {
      const payload = {
        name: data.name,
        title: data.title,
        description:
          typeof data.description === "string" ? data.description : "",
      };

      if (mode === "create") {
        await api.genericApiRequest({
          entity: "todoTemplate",
          method: "post",
          submitData: payload,
          successHandler: () => {
            onSuccess();
            onClose();
          },
          failHandler: (error: any) => {
            handleError(error);
          },
        });
      } else {
        await api.genericApiRequest({
          entity: "todoTemplate",
          method: "put",
          entityId: initialData.id,
          submitData: payload,
          successHandler: () => {
            onSuccess();
            onClose();
          },
          failHandler: (error: any) => {
            handleError(error);
          },
        });
      }
    } catch (error: any) {
      handleError(error);
    }
  };

  const handleClose = () => {
    reset();
    handleError("");
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { maxHeight: "75%" } }}
    >
      <DialogTitle className={styles.dialogTitle}>
        {mode === "create"
          ? t("create_new_todo_template")
          : t("edit_todo_template")}
      </DialogTitle>
      <form
        noValidate
        onSubmit={handleSubmit(onSubmit, (errors) => {
          focusFirstInvalidField(errors);
        })}
      >
        <DialogContent>
          <Stack spacing={1.5}>
            <Box ref={registerValidationTarget("name")}>
              <Controller
                name="name"
                control={control}
                rules={{
                  validate: (value) =>
                    Boolean(value?.trim()) || t("field_required"),
                }}
                render={({ field }) => (
                  <>
                    <InputWithRef {...field} label={t("name")} required />
                    {errors.name && (
                      <Typography color="error" variant="caption">
                        {errors.name.message}
                      </Typography>
                    )}
                  </>
                )}
              />
            </Box>

            <Box ref={registerValidationTarget("title")}>
              <Controller
                name="title"
                control={control}
                rules={{
                  validate: (value) =>
                    Boolean(value?.trim()) || t("field_required"),
                }}
                render={({ field }) => (
                  <>
                    <InputWithRef {...field} label={t("title")} required />
                    {errors.title && (
                      <Typography color="error" variant="caption">
                        {errors.title.message}
                      </Typography>
                    )}
                  </>
                )}
              />
            </Box>

            <Box ref={registerValidationTarget("description")}>
              <Controller
                name="description"
                control={control}
                rules={{
                  validate: (value) =>
                    Boolean(value?.trim()) || t("field_required"),
                }}
                render={({ field }) => (
                  <>
                    <TextAreaWithRef
                      {...field}
                      label={t("description")}
                      rows={4}
                      required
                      fieldStyle={{ resize: "vertical" }}
                    />
                    {errors.description && (
                      <Typography color="error" variant="caption">
                        {errors.description.message}
                      </Typography>
                    )}
                  </>
                )}
              />
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions className={styles.dialogActionsContainer}>
          <GenericButton variant="outlined" onClick={handleClose}>
            {t("cancel")}
          </GenericButton>
          <GenericButton
            variant="contained"
            type="submit"
            waitingForResponse={true}
            showSpinnerOnDisable={true}
            disabled={mode === "edit" && !isDirty}
          >
            {mode === "create" ? t("add") : t("save")}
          </GenericButton>
        </DialogActions>
      </form>
      {errorModal}
    </Dialog>
  );
}
